import React from 'react';
import './styleTableProductos.css';
import { Button, Space, Table, Tag, Tooltip, Typography, Dropdown } from 'antd';
import {
  EditOutlined, StopOutlined, CheckCircleOutlined, DeleteOutlined,
  PrinterOutlined, MoreOutlined, EyeInvisibleOutlined, EyeOutlined, DownOutlined, SettingOutlined
} from '@ant-design/icons';
import { usePermission } from '../../hooks/usePermission';
import { useAuth } from '../../aws/AuthContext';

const { Text } = Typography;

const TableProductos = ({ dataProductos, proveedoresDB = [], onEdit, onDelete, onPrint, loading, onStatusChange, scroll }) => {

  const { hasPermission } = usePermission();
  const { session } = useAuth();

  const columns = [
    {
      title: 'NOMBRE DEL PRODUCTO',
      width: 250,
      dataIndex: 'nombre',
      key: 'nombre',
      fixed: 'left',
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Space size={4} style={{ marginTop: '2px' }}>
            {record.es_combo && <Tag color="green" style={{ margin: 0 }}>COMBO</Tag>}
            {record.ocultar_en_ventas && <Tag color="orange" style={{ margin: 0 }}>Oculto en POS</Tag>}
          </Space>
        </Space>
      )
    },
    {
      title: 'DESCRIPCIÓN',
      width: 250,
      dataIndex: 'descripcion',
      key: 'descripcion',
    },
    {
      title: 'CÓDIGO DE BARRAS',
      width: 200,
      dataIndex: 'codigo_barras',
      key: 'codigo_barras',
      render: (texto, record) => record.has_variants ? <Text type="secondary" italic>Múltiples Códigos</Text> : texto || '--'
    },
    {
      title: 'CÓDIGO INTERNO',
      width: 110,
      dataIndex: 'codigo_interno',
      key: 'codigo_interno',
      render: (texto, record) => record.has_variants ? <Text type="secondary" italic>Múltiples</Text> : texto || '--'
    },
    !hasPermission('productos:precio_compra') ? {
      title: 'COSTO COMPRA',
      dataIndex: 'precio_compra',
      key: 'precio_compra',
      width: 150,
      render: (texto, record) => record.has_variants
        ? <Text type="secondary" italic>Múltiple</Text>
        : `$ ${Number(texto || 0).toLocaleString('es-CO')}`
    } : session?.rol?.esCEO ? {
      title: 'COSTO COMPRA',
      dataIndex: 'precio_compra',
      key: 'precio_compra',
      width: 150,
      render: (texto, record) => record.has_variants
        ? <Text type="secondary" italic>Múltiple</Text>
        : `$ ${Number(texto || 0).toLocaleString('es-CO')}`
    } : null,
    {
      title: 'PRECIO VENTA',
      dataIndex: 'precio_venta',
      key: 'precio_venta',
      width: 150,
      render: (texto, record) => {
        // Lógica de "Precio Desde" para variantes
        if (record.has_variants && record.variantes && record.variantes.length > 0) {
          const precioMinimo = Math.min(...record.variantes.map(v => Number(v.precio_base || 0)));
          return (
            <Text style={{ color: '#22BABB', fontWeight: 'bold' }}>
              <span style={{ fontSize: '11px', color: '#94a3b8', marginRight: '4px', fontWeight: 'normal' }}>Desde</span>
              $ {precioMinimo.toLocaleString('es-CO')}
            </Text>
          );
        }
        // Precio normal para productos simples
        return (
          <Text style={{ color: '#22BABB', fontWeight: 'bold' }}>
            $ {Number(texto || 0).toLocaleString('es-CO')}
          </Text>
        );
      }
    },
    {
      title: 'IVA (%)',
      dataIndex: 'iva',
      key: 'iva',
      width: 100,
      render: (texto) => `${texto || 0}%`
    },
    {
      title: 'DESCUENTO (%)',
      dataIndex: 'descuento',
      key: 'descuento',
      width: 130,
      render: (texto, record) => (
        <Text type={texto > 0 ? "success" : "secondary"}>
          {texto || 0} % {record.has_variants && <span style={{ fontSize: '10px', color: '#94a3b8', display: 'block' }}></span>}
        </Text>
      )
    },
    {
      title: 'STOCK',
      dataIndex: 'stock',
      key: 'stock',
      width: 110,
      render: (texto, record) => {
        if (record.es_combo) {
          return <Tooltip title="El inventario de este combo se calcula basado en sus ingredientes disponibles."><Text type="secondary" italic>Dinámico</Text></Tooltip>;
        }

        const stockReal = record.has_variants && record.variantes
          ? record.variantes.reduce((acumulado, v) => acumulado + Number(v.stock || 0), 0)
          : Number(record.stock || 0);

        return (
          <Text style={{ color: stockReal <= 5 ? '#cf1322' : 'inherit', fontWeight: stockReal <= 5 ? 'bold' : 'normal' }}>
            {stockReal.toLocaleString('es-CO')}
            {record.has_variants && <small style={{ fontSize: '10px' }}>(Total)</small>}
          </Text>
        );
      }
    },
    {
      title: 'CATEGORÍA',
      width: 150,
      dataIndex: 'categoria',
      key: 'categoria',
    },
    {
      title: 'UNIDAD MEDIDA',
      width: 150,
      dataIndex: 'unidad_medida',
      key: 'unidad_medida',
    },
    {
      title: 'PRESENTACIÓN',
      width: 150,
      dataIndex: 'presentacion',
      key: 'presentacion',
      render: (texto, record) => record.has_variants ? <Text style={{ color: '#0958d9', fontWeight: '500' }}>Varias Presentaciones</Text> : texto || '--'
    },
    {
      title: 'MARCA',
      width: 150,
      dataIndex: 'marca',
      key: 'marca',
    },
    {
      title: 'UBICACIÓN',
      width: 150,
      dataIndex: 'ubicacion',
      key: 'ubicacion',
    },
    {
      title: 'PROVEEDOR',
      width: 150,
      dataIndex: 'proveedor_id',
      key: 'proveedor_id',
      render: (id) => {
        if (!id) return <Text type="secondary">--</Text>;
        const proveedorEncontrado = proveedoresDB.find(p => p.id === id);
        return proveedorEncontrado ? proveedorEncontrado.nombre : <Text type="danger">Desconocido</Text>;
      }
    },
    {
      title: 'ESTADO',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <span style={{ color: status === 'active' ? '#389e0d' : '#cf1322', fontWeight: 'bold' }}>
          {status === 'active' ? 'ACTIVO' : 'INACTIVO'}
        </span>
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 90,
      align: 'center',
      render: (_, record) => {
        const menuItems = [
          {
            key: '1',
            label: 'Editar Producto',
            icon: <EditOutlined />,
            disabled: !hasPermission('productos:editar'),
            onClick: () => onEdit(record)
          },
          {
            key: '2',
            label: 'Imprimir Etiqueta',
            icon: <PrinterOutlined />,
            onClick: () => onPrint(record)
          },
          {
            key: '3',
            label: record.ocultar_en_ventas ? 'Mostrar en POS' : 'Ocultar en POS',
            icon: record.ocultar_en_ventas ? <EyeOutlined /> : <EyeInvisibleOutlined />,
            disabled: !hasPermission('productos:editar'),
            onClick: () => onStatusChange({ ...record, ocultar_en_ventas: !record.ocultar_en_ventas }) // Reutilizamos tu función de status
          },
          { type: 'divider' },
          {
            key: '4',
            label: record.status === 'active' ? 'Desactivar' : 'Activar',
            icon: record.status === 'active' ? <StopOutlined /> : <CheckCircleOutlined />,
            disabled: !hasPermission('productos:desactivar'),
            onClick: () => onStatusChange({ ...record, status: record.status === 'active' ? 'inactive' : 'active' })
          },
          {
            key: '5',
            label: <Text type="danger">Eliminar</Text>,
            icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />,
            disabled: !hasPermission('productos:eliminar'),
            onClick: () => onDelete(record)
          }
        ];

        return (
          <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
            <Button 
              type="text" 
              shape="circle" 
              icon={<SettingOutlined style={{ fontSize: '20px' }} />} 
              style={{ 
                color: '#22BABB', 
                backgroundColor: '#f0fbfb',
                border: '1px solid #bcebf5'
              }} 
            />
          </Dropdown>
        );
      },
    },
  ].filter(Boolean);

  return (
    <Table
      columns={columns}
      dataSource={dataProductos}
      rowClassName={(record) => record.status === 'inactive' ? 'row-inactivo' : ''}
      loading={loading}
      rowKey="id"
      scroll={scroll || { x: 1500, y: 500 }}
      size='middle'
      pagination={{
        defaultPageSize: 50,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50', '100', '200'],
        showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} productos`
      }}
    />
  )
}

export default TableProductos;